import React, { useState, useEffect, useRef } from 'react';
import { Bell, CheckCheck, Trash2, X, FileText, CheckCircle, Clock, AlertTriangle } from 'lucide-react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const TYPE_CONFIG = {
  CONTRACT_REVIEW:   { icon: Clock,         cls: 'text-amber-600',  bg: 'bg-amber-50' },
  CONTRACT_APPROVED: { icon: CheckCircle,   cls: 'text-green-600',  bg: 'bg-green-50' },
  CONTRACT_REJECTED: { icon: X,             cls: 'text-red-600',    bg: 'bg-red-50' },
  CONTRACT_EXPIRY:   { icon: AlertTriangle, cls: 'text-orange-600', bg: 'bg-orange-50' },
  GENERAL:           { icon: Bell,          cls: 'text-blue-600',   bg: 'bg-blue-50' },
};

export default function NotificationsPanel() {
  const { user, systemSettings } = useAuth();
  const tz = systemSettings?.timezone || 'America/Los_Angeles';
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const panelRef = useRef(null);

  const loadNotifications = async () => {
    if (!user) return;
    try {
      const res = await api.get('/api/notifications');
      setNotifications(res.data.data);
      setUnreadCount(res.data.unread_count);
    } catch {}
  };

  const pollUnreadCount = async () => {
    if (!user) return;
    try {
      const res = await api.get('/api/notifications/unread-count');
      setUnreadCount(res.data.count);
    } catch {}
  };

  // Poll every 30 seconds — pause when tab is hidden
  useEffect(() => {
    let interval = null;
    const start = () => {
      if (interval) clearInterval(interval);
      if (!document.hidden) {
        interval = setInterval(pollUnreadCount, 30000);
      }
    };
    const handleVisibility = () => {
      if (document.hidden) {
        if (interval) { clearInterval(interval); interval = null; }
      } else {
        pollUnreadCount();
        start();
      }
    };
    pollUnreadCount();
    start();
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      if (interval) clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (open) loadNotifications();
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const markRead = async (id) => {
    try {
      await api.put(`/api/notifications/${id}/read`);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch {}
  };

  const markAllRead = async () => {
    try {
      await api.put('/api/notifications/mark-all-read');
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch {}
  };

  const deleteNotification = async (id) => {
    try {
      await api.delete(`/api/notifications/${id}`);
      const deleted = notifications.find(n => n.id === id);
      setNotifications(prev => prev.filter(n => n.id !== id));
      if (deleted && !deleted.read) setUnreadCount(prev => Math.max(0, prev - 1));
    } catch {}
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const diff = (Date.now() - new Date(dateStr).getTime()) / 1000;
    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return new Date(dateStr).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', timeZone: tz });
  };

  return (
    <div className="relative" ref={panelRef}>
      <Button
        variant="ghost" size="sm"
        className="relative"
        onClick={() => setOpen(!open)}
        aria-label={`Notifications${unreadCount > 0 ? `, ${unreadCount} unread` : ''}`}
        aria-expanded={open}
        data-testid="notifications-button"
      >
        <Bell className="h-5 w-5" aria-hidden="true" />
        {unreadCount > 0 && (
          <span className="absolute -top-1.5 -right-2 flex min-w-[18px] h-[18px] items-center justify-center rounded-full bg-destructive px-1 text-[9px] font-bold text-white leading-none" aria-hidden="true">
            {unreadCount > 999 ? '999+' : unreadCount}
          </span>
        )}
        <span className="sr-only" aria-live="polite">{unreadCount > 0 ? `${unreadCount} unread notifications` : ''}</span>
      </Button>

      {open && (
        <div
          className="absolute right-0 top-full mt-2 w-96 rounded-xl border bg-card shadow-xl z-50"
          role="dialog" aria-label="Notifications panel"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b">
            <div className="flex items-center gap-2">
              <Bell className="h-4 w-4 text-primary" />
              <span className="font-semibold text-sm">Notifications</span>
              {unreadCount > 0 && (
                <Badge className="text-xs bg-primary">{unreadCount} new</Badge>
              )}
            </div>
            <div className="flex items-center gap-1">
              {unreadCount > 0 && (
                <Button size="sm" variant="ghost" onClick={markAllRead} className="text-xs h-7 px-2">
                  <CheckCheck className="h-3.5 w-3.5 mr-1" /> Mark all read
                </Button>
              )}
              <Button size="sm" variant="ghost" onClick={() => setOpen(false)} aria-label="Close">
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Notification list */}
          <div className="max-h-[420px] overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 text-center">
                <Bell className="h-8 w-8 text-muted-foreground/30 mb-2" />
                <p className="text-sm text-muted-foreground">No notifications</p>
                <p className="text-xs text-muted-foreground">You're all caught up!</p>
              </div>
            ) : (
              notifications.map(n => {
                const cfg = TYPE_CONFIG[n.type] || TYPE_CONFIG.GENERAL;
                const Icon = cfg.icon;
                return (
                  <div
                    key={n.id}
                    className={`flex gap-3 px-4 py-3 border-b last:border-0 transition-colors cursor-pointer ${
                      !n.read ? 'bg-primary/5 hover:bg-primary/10' : 'hover:bg-accent/50'
                    }`}
                    onClick={() => { if (!n.read) markRead(n.id); }}
                  >
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${cfg.bg}`}>
                      <Icon className={`h-4 w-4 ${cfg.cls}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className={`text-sm leading-snug ${!n.read ? 'font-semibold' : ''}`}>{n.title}</p>
                        {!n.read && <div className="w-2 h-2 rounded-full bg-primary flex-shrink-0 mt-1.5" aria-label="Unread" />}
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{n.message}</p>
                      <p className="text-[10px] text-muted-foreground mt-1">{formatTime(n.created_at)}</p>
                    </div>
                    <Button
                      size="sm" variant="ghost"
                      className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 flex-shrink-0"
                      onClick={e => { e.stopPropagation(); deleteNotification(n.id); }}
                      aria-label="Delete notification"
                    >
                      <Trash2 className="h-3 w-3 text-muted-foreground" />
                    </Button>
                  </div>
                );
              })
            )}
          </div>

          {notifications.length > 0 && (
            <div className="px-4 py-2 border-t">
              <p className="text-xs text-center text-muted-foreground">
                {notifications.length} notification{notifications.length !== 1 ? 's' : ''} &mdash; click to mark as read
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
